// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useState } from 'react';
import { Alert, AppIcon, Spinner } from '@loa/ui-components';
import {
  WizardSectionForm,
  type AnswerMap,
  type SectionError,
  type WizardSchema,
  type WizardSection,
} from '@loa/fact-find-form';
import { Card } from '../Card';
import { ProfileFormTab } from './ProfileFormTab';
import { ProviderOptionsSelect } from './ProviderOptionsSelect';
import { profileFormApi, type ProfileFormDraft } from '../../api/profileFormApi';
import { clientApi } from '../../api/clientApi';
import { providerApi } from '../../api/providerApi';
import type { ProviderSummaryDTO } from '../../types/provider';

interface Props {
  profileId: string;
}

interface ClientOption {
  id: string;
  name: string;
}

function isAxios409(e: unknown): boolean {
  return (
    typeof e === 'object' &&
    e !== null &&
    (e as { response?: { status?: number } }).response?.status === 409
  );
}

/**
 * Editable Fact Find for the advisor ASSIGNED to the relationship: the same
 * schema-DSL-v2 per-section editor the client uses ({@link WizardSectionForm}
 * from @loa/fact-find-form), but presented as collapsible per-section boxes
 * instead of the client's stepper wizard. Advisor + client share the one live
 * draft; each box saves its section independently (per-section save → the
 * server appends an immutable version once the document is COMPLETE).
 *
 * No integration/chat slots are passed, so OPEN_BANKING/CHAT sections render as
 * plain form fields for the advisor. A DSL v1 brokerage override has no
 * sectioned draft (the server answers 409) — we fall back to the read-only
 * {@link ProfileFormTab}.
 */
export function FactFindAccordion({ profileId }: Readonly<Props>) {
  const [schema, setSchema] = useState<WizardSchema | null>(null);
  const [draft, setDraft] = useState<ProfileFormDraft | null>(null);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [providers, setProviders] = useState<ProviderSummaryDTO[]>([]);
  const [legacy, setLegacy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [errorsById, setErrorsById] = useState<Record<string, SectionError[]>>({});
  const [savedNote, setSavedNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, d, cs, ps] = await Promise.all([
        profileFormApi.getSchema(profileId, 'FACT_FIND'),
        profileFormApi.getDraft(profileId, 'FACT_FIND'),
        clientApi.getClientsByProfile(profileId),
        // Best-effort: a providers lookup failure must not break the form — the
        // tagged fields just degrade to an empty dropdown / kept value.
        providerApi.getProviders().catch(() => [] as ProviderSummaryDTO[]),
      ]);
      setSchema(s as WizardSchema);
      setDraft(d);
      setProviders(ps);
      setClients(
        cs.map((c) => ({
          id: c.id,
          name: `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || 'Client',
        })),
      );
      setOpenId((s.sections ?? [])[0]?.id ?? null);
    } catch (e) {
      if (isAxios409(e)) {
        setLegacy(true);
      } else {
        setError('Could not load this form.');
      }
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div data-testid="ff-accordion-loading" className="py-8 flex justify-center">
        <Spinner />
      </div>
    );
  }
  // DSL v1 brokerage override → no sectioned draft; show the read-only view.
  if (legacy) {
    return <ProfileFormTab profileId={profileId} formType="FACT_FIND" />;
  }
  if (error || !schema || !draft) {
    return (
      <div data-testid="ff-accordion-error">
        <Alert variant="error">{error ?? 'Could not load this form.'}</Alert>
      </div>
    );
  }

  const sections = schema.sections ?? [];

  const save = async (section: WizardSection, answers: AnswerMap) => {
    setSavingId(section.id);
    setErrorsById((m) => ({ ...m, [section.id]: [] }));
    setSavedNote(null);
    try {
      const updated = await profileFormApi.saveSection(profileId, 'FACT_FIND', section.id, answers);
      setDraft(updated);
      setSavedNote(`${section.title} saved.`);
    } catch (e) {
      const data = (e as { response?: { data?: { fieldErrors?: SectionError[]; message?: string } } })
        .response?.data;
      setErrorsById((m) => ({
        ...m,
        [section.id]: data?.fieldErrors?.length
          ? data.fieldErrors
          : [{ questionId: section.id, message: data?.message ?? 'Save failed — please try again.' }],
      }));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Card>
      <div data-testid="ff-accordion" className="font-sans flex flex-col gap-[12px]">
        <p className="text-body-03 text-[#6b7280] m-0">
          You can complete or update this fact find on the client&apos;s behalf — the client edits the
          same form.
        </p>
        {savedNote && (
          <output
            data-testid="ff-accordion-saved"
            className="block p-[10px] rounded-lg bg-[#E8F8EE] text-[#00692a] text-body-03"
          >
            {savedNote}
          </output>
        )}
        {sections.map((section) => {
          const status = draft.sectionStatus?.[section.id] ?? 'NOT_STARTED';
          const open = openId === section.id;
          return (
            <section
              key={section.id}
              className="border border-[#E1E1E1] rounded-button overflow-hidden"
              data-testid={`ff-accordion-section-${section.id}`}
            >
              <button
                type="button"
                className="w-full flex items-center justify-between bg-white border-0 cursor-pointer px-md py-[14px]"
                onClick={() => setOpenId(open ? null : section.id)}
                aria-expanded={open}
                data-testid={`ff-accordion-toggle-${section.id}`}
              >
                <span className="flex items-center gap-[10px]">
                  <AppIcon
                    name={status === 'COMPLETE' ? 'check circle' : 'status clock'}
                    size={18}
                    color={status === 'COMPLETE' ? '#00692a' : '#9ca3af'}
                  />
                  <span className="text-body-02 font-medium text-black">{section.title}</span>
                </span>
                <AppIcon name={open ? 'chevron up' : 'chevron down'} size={18} />
              </button>
              {open && (
                <div className="px-md pb-md border-t border-[#F0F0F0]">
                  <WizardSectionForm
                    section={section}
                    clients={clients}
                    initialAnswers={(draft.answers?.[section.id] as AnswerMap) ?? {}}
                    saving={savingId === section.id}
                    errors={errorsById[section.id] ?? []}
                    onSave={(a) => save(section, a)}
                    renderOptionsSelect={(args) =>
                      args.field.optionsSource?.type === 'PROVIDERS' ? (
                        <ProviderOptionsSelect {...args} providers={providers} />
                      ) : null
                    }
                    hideChrome
                  />
                </div>
              )}
            </section>
          );
        })}
      </div>
    </Card>
  );
}
